/**
 * Hand history, and the proof that goes with it.
 *
 * Two screens in one: a list of the player's own hands, and one hand replayed
 * with the shuffle laid open beside it. They belong together — "what happened?"
 * and "was it dealt honestly?" are the same question, and separating them would
 * turn the second into a feature nobody finds.
 *
 * The verification runs **here, in the browser**, on the player's own machine,
 * using the same `@pf/fairness` the server deals with. That is the entire point:
 * a server that says "we checked and it was fine" has proved nothing. The page
 * takes the seed, derives the deck itself, and compares — and if the answer were
 * ever no, this code would say so about the room that shipped it.
 */

import { useCallback, useEffect, useState } from 'react';

import { commitment as hashSeed, deriveDeck, fromHex } from '@pf/fairness';

import { bb } from '../fa.ts';
import { cutIntoStreets, positionsOf, type WireEvent } from '../replay.ts';
import { Avatar } from './avatar.tsx';
import { PlayingCard } from './cards.tsx';
import { HomeLink } from './HomeLink.tsx';
import { api } from '../server.ts';

interface Summary {
  handId: string;
  tableId: string;
  tableName: string | null;
  handNumber: number;
  smallBlind: number;
  bigBlind: number;
  playedAt: string;
  seat: number;
  net: number;
  won: number;
  cards: string[] | null;
  revealed: boolean;
  winners: string[];
  pot: number;
}

interface Fairness {
  handId: string;
  commitment: string;
  clientSeeds: { seat: number; seed: string }[];
  serverSeed: string | null;
  deck: number[] | null;
  revealed: boolean;
  revealsAt: string | null;
}

interface Detail extends Summary {
  buttonSeat: number;
  events: WireEvent[];
  fairness: Fairness;
  seats: HandSeat[];
}

interface HandSeat {
  seat: number;
  name: string;
  committed: number;
  won: number;
  net: number;
}



/**
 * `embedded` renders the same thing inside a drawer over the table.
 *
 * A player asks "what just happened?" while still sitting down, and sending
 * them to another page to answer it loses their seat. Same component, same
 * data, minus the chrome that belongs to a page of its own.
 */
export function History({ session, embedded = false }: { session: string | null; embedded?: boolean }) {
  const [hands, setHands] = useState<Summary[] | null>(null);
  const [open, setOpen] = useState<Detail | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (session === null) {
      setError('ابتدا وارد شوید.');
      return;
    }
    try {
      const response = await fetch(api('/api/hands?limit=50'), {
        headers: { authorization: `Bearer ${session}` },
        cache: 'no-store',
      });
      if (!response.ok) {
        setError(response.status === 401 ? 'نشست شما منقضی شده. دوباره وارد شوید.' : `خطا ${response.status}`);
        return;
      }
      const body = (await response.json()) as { hands: Summary[] };
      setHands(body.hands);
      setError(null);
    } catch {
      setError('سرور در دسترس نیست.');
    }
  }, [session]);

  useEffect(() => {
    void load();
  }, [load]);

  const openHand = async (handId: string): Promise<void> => {
    if (session === null) return;
    const response = await fetch(api(`/api/hands/${handId}`), {
      headers: { authorization: `Bearer ${session}` },
      cache: 'no-store',
    });
    if (response.ok) setOpen((await response.json()) as Detail);
  };

  if (error !== null) {
    return (
      <div className={embedded ? "history embedded" : "lobby"}>
        {!embedded && <h1>Hand History</h1>}
        <div className="error">{error}</div>
        <p className="sub">
          <a href="/">بازگشت به اتاق</a>
        </p>
      </div>
    );
  }
  if (hands === null)
    return (
      <div className={embedded ? "history embedded" : "lobby"}>
        <p className="sub">در حال خواندن…</p>
      </div>
    );

  return (
    <div className={embedded ? "history embedded" : "lobby history"}>
      {!embedded && (
        <div className="account-bar">
          <span className="who">Hand History</span>
          <span className="spacer" />
          <HomeLink />
        </div>
      )}

      {hands.length === 0 ? (
        <p className="sub">هنوز دستی بازی نکرده‌اید.</p>
      ) : (
        <div className="hand-list">
          {/* The three things that identify a hand to the person who played it:
              what they held, who took it, and how big it was. The seat number
              and the table id are not among them. */}
          <div className="hand-head lat-ui">
            <span>My Cards</span>
            <span>Winner</span>
            <span>Pot</span>
            <span />
          </div>
          {hands.map((hand) => (
            <button key={hand.handId} className="hand-row" onClick={() => void openHand(hand.handId)}>
              <span className="h-cards">
                {hand.cards === null ? (
                  <span className="quiet">—</span>
                ) : (
                  hand.cards.map((card) => <MiniCard key={card} code={card} />)
                )}
              </span>
              <span className="h-winner">
                {hand.winners.length === 0 ? <span className="quiet">—</span> : hand.winners.join(' + ')}
                <span className="h-when lat-ui">
                  {formatWhen(hand.playedAt)} · {hand.tableName ?? hand.tableId}
                </span>
              </span>
              <span className="h-pot num lat-ui">{bb(hand.pot, hand.bigBlind)}</span>
              <bdi className={`h-net lat-ui ${hand.net > 0 ? 'up' : hand.net < 0 ? 'down' : ''}`}>
                {hand.net > 0 ? '+' : hand.net < 0 ? '−' : ''}
                {bb(Math.abs(hand.net), hand.bigBlind)}
              </bdi>
            </button>
          ))}
        </div>
      )}

      {open !== null && <HandDetail detail={open} onClose={() => setOpen(null)} />}
    </div>
  );
}

/**
 * One hand, laid out the way it was played.
 *
 * The old version was the action feed with a scrollbar — every event on its own
 * line, in the order the server emitted them, which is the order a machine
 * cares about. A player reading their own hand back is asking a different
 * question: what did each street cost, and who did what on it. So the hand is
 * cut into its four streets, side by side, each with what the pot stood at when
 * it ended, and every action carries the face and the position of the person
 * who made it.
 *
 * Position matters more than the seat number and was not shown at all. "Seat 4
 * raised" tells you nothing; "the button raised" is the whole hand.
 */
function HandDetail({ detail, onClose }: { detail: Detail; onClose: () => void }) {
  const board = detail.events
    .filter((event): event is Extract<WireEvent, { type: 'street-dealt' }> => event.type === 'street-dealt')
    .flatMap((event) => event.cards);

  const nameOf = (seat: number): string =>
    detail.seats.find((entry) => entry.seat === seat)?.name ?? `Seat ${seat + 1}`;
  const places = positionsOf(detail.seats.map((entry) => entry.seat), detail.buttonSeat);
  const shown = new Map(
    detail.events
      .filter((event): event is Extract<WireEvent, { type: 'showdown' }> => event.type === 'showdown')
      .flatMap((event) => event.shown)
      .map((entry) => [entry.seat, entry.cards]),
  );

  return (
    <div className="overlay" onClick={onClose}>
      <div className="dialog replay-sheet" onClick={(event) => event.stopPropagation()}>
        <div className="replay-head">
          <div>
            <h2>
              {detail.tableName ?? detail.tableId} · Hand #{detail.handNumber}
            </h2>
            <p className="hint">
              {formatWhen(detail.playedAt)} · {detail.smallBlind}/{detail.bigBlind} ·{' '}
              <span className="lat-ui mono">{detail.handId.slice(0, 8)}</span>
            </p>
          </div>
          <span className="spacer" />
          <button className="btn-plain small" onClick={onClose}>
            Close
          </button>
        </div>

        {/* The board, then the people. Big cards on purpose: this is the one
            screen where a player is looking at the cards rather than acting
            on them, and there is room. */}
        <div className="replay-felt">
          <div className="replay-board">
            {board.length === 0 ? (
              <span className="quiet">هیچ کارت مشترکی پخش نشد</span>
            ) : (
              board.map((card, index) => <PlayingCard key={`${card}-${index}`} code={card} size="hole" />)
            )}
          </div>
          <div className="replay-pot lat-ui">
            <span>Pot</span>
            <b className="num">{bb(detail.pot, detail.bigBlind)}</b>
          </div>
        </div>

        <div className="replay-seats">
          {detail.seats.map((entry) => {
            const cards = entry.seat === detail.seat ? detail.cards : (shown.get(entry.seat) ?? null);
            return (
              <div
                key={entry.seat}
                className={`replay-seat${entry.seat === detail.seat ? ' you' : ''}${entry.won > 0 ? ' won' : ''}`}
              >
                <Avatar name={entry.name} />
                <div className="who">
                  <div className="name">{entry.name}</div>
                  <div className="pos lat-ui">{places.get(entry.seat) ?? ''}</div>
                </div>
                <div className="cards">
                  {cards === null ? (
                    <span className="quiet">—</span>
                  ) : (
                    cards.map((card) => <MiniCard key={card} code={card} />)
                  )}
                </div>
                <bdi className={`net lat-ui ${entry.net > 0 ? 'up' : entry.net < 0 ? 'down' : ''}`}>
                  {entry.net > 0 ? '+' : entry.net < 0 ? '−' : ''}
                  {bb(Math.abs(entry.net), detail.bigBlind)}
                </bdi>
              </div>
            );
          })}
        </div>

        <div className="streets">
          {cutIntoStreets(detail.events).map((street) => (
            <section key={street.name} className="street">
              <header>
                <span className="lat-ui">{STREET_TITLE[street.name] ?? street.name}</span>
                <b className="num lat-ui">{bb(street.pot, detail.bigBlind)}</b>
              </header>
              {street.cards.length > 0 && (
                <div className="street-cards">
                  {street.cards.map((card) => (
                    <MiniCard key={card} code={card} />
                  ))}
                </div>
              )}
              {street.actions.length === 0 ? (
                <p className="quiet">—</p>
              ) : (
                street.actions.map((action, index) => (
                  <div key={index} className="act">
                    <Avatar name={nameOf(action.seat)} />
                    <div className="who">
                      <div className="name">{nameOf(action.seat)}</div>
                      <div className="amount num lat-ui">
                        {action.amount === null ? '' : bb(action.amount, detail.bigBlind)}
                      </div>
                    </div>
                    <div className="badges">
                      {places.has(action.seat) && (
                        <span className="pos lat-ui">{places.get(action.seat)}</span>
                      )}
                      <span className={`verb lat-ui ${action.kind}`}>{action.label}</span>
                    </div>
                  </div>
                ))
              )}
            </section>
          ))}
        </div>

        <Verify fairness={detail.fairness} />
      </div>
    </div>
  );
}

const STREET_TITLE: Record<string, string> = {
  preflop: 'Pre-Flop',
  flop: 'Flop',
  turn: 'Turn',
  river: 'River',
};


type Check = { state: 'idle' | 'working' } | { state: 'done'; commitmentOk: boolean; deckOk: boolean };

/**
 * The verification, done here rather than taken on trust.
 *
 * Both halves fail differently and are reported separately. A commitment that
 * does not match means the seed we published is not the one we promised before
 * the deal — the room changed its mind. A deck that does not match means the
 * seed is genuine but the cards did not come from it — the room dealt something
 * else. They are different accusations and a player deserves to know which.
 */
function Verify({ fairness }: { fairness: Fairness }) {
  const [check, setCheck] = useState<Check>({ state: 'idle' });

  const run = async (): Promise<void> => {
    if (fairness.serverSeed === null || fairness.deck === null) return;
    setCheck({ state: 'working' });

    const seed = fromHex(fairness.serverSeed);
    const [derivedCommitment, derivedDeck] = await Promise.all([
      hashSeed(seed),
      deriveDeck({
        serverSeed: seed,
        handId: fairness.handId,
        clientSeeds: fairness.clientSeeds,
      }),
    ]);

    setCheck({
      state: 'done',
      commitmentOk: derivedCommitment === fairness.commitment.toLowerCase(),
      deckOk:
        derivedDeck.length === fairness.deck.length &&
        derivedDeck.every((card, index) => card === fairness.deck?.[index]),
    });
  };

  return (
    <div className="verify">
      <div className="feed-head lat-ui">Provable Shuffle</div>

      <Row label="Commitment" value={fairness.commitment} />
      {fairness.clientSeeds.length > 0 && (
        <Row
          label="Player seeds"
          value={fairness.clientSeeds.map((entry) => `${entry.seat}:${entry.seed}`).join('  ')}
        />
      )}

      {fairness.revealed ? (
        <>
          <Row label="Server seed" value={fairness.serverSeed ?? ''} />
          <p className="hint">
            این بررسی روی همین دستگاه شما اجرا می‌شود — دسته‌کارت را از بذر بازتولید می‌کند و با
            آنچه پخش شده مقایسه می‌کند. حرف سرور در آن نقشی ندارد.
          </p>
          {check.state === 'done' ? (
            <div className={`verdict${check.commitmentOk && check.deckOk ? ' ok' : ' bad'}`}>
              <div>
                {check.commitmentOk ? '✓' : '✗'} بذر با تعهدی که پیش از پخش منتشر شده بود می‌خواند
              </div>
              <div>{check.deckOk ? '✓' : '✗'} دسته‌کارت دقیقاً از همان بذر ساخته شده</div>
            </div>
          ) : (
            <button className="btn-check small" onClick={() => void run()} disabled={check.state === 'working'}>
              {check.state === 'working' ? '…' : 'Verify This Shuffle'}
            </button>
          )}
        </>
      ) : (
        <p className="hint">
          {/* Said plainly, because "we are not telling you yet" needs a reason. */}
          بذر این دست هنوز منتشر نشده. انتشار زودهنگام یعنی کارت‌های فولدشده‌ی بقیه هم لو می‌رود —
          چیزی که پوکر واقعی به کسی نمی‌دهد. تعهدش از قبل منتشر شده و قابل تغییر نیست؛ بذر{' '}
          {/* Isolated, or the bidi algorithm reorders an English date inside a
              Persian sentence into something that reads as a different time. */}
          {fairness.revealsAt !== null ? <bdi className="lat-ui">{formatWhen(fairness.revealsAt)}</bdi> : 'بعداً'}{' '}
          منتشر می‌شود.
        </p>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="kv">
      <span className="k lat-ui">{label}</span>
      <span className="v lat-ui mono">{value}</span>
    </div>
  );
}

const SUIT_CLASS: Record<string, string> = { s: 'spade', h: 'heart', d: 'diamond', c: 'club' };
const SUIT_GLYPH: Record<string, string> = { s: '♠', h: '♥', d: '♦', c: '♣' };

function MiniCard({ code }: { code: string }) {
  const rank = code.slice(0, -1);
  const suit = code.slice(-1);
  return (
    <span className={`mini ${SUIT_CLASS[suit] ?? ''}`}>
      {rank}
      {SUIT_GLYPH[suit] ?? suit}
    </span>
  );
}

/** One line of the replay, in the language the feed already speaks. */
function describe(event: WireEvent, bigBlind: number): string {
  switch (event.type) {
    case 'hand-started':
      return `— Hand #${event.handNumber} —`;
    case 'blind-posted':
      return `صندلی ${event.seat + 1} — ${event.blind === 'small' ? 'SB' : 'BB'} ${bb(event.amount, bigBlind)}`;
    case 'hole-cards-dealt':
      return event.cards === null
        ? `صندلی ${event.seat + 1} — کارت گرفت`
        : `صندلی ${event.seat + 1} — ${event.cards.join(' ')}`;
    case 'acted': {
      const amount = event.action === 'fold' || event.action === 'check' ? '' : ` ${bb(event.to, bigBlind)}`;
      return `صندلی ${event.seat + 1} — ${event.allIn ? 'All In' : event.action}${amount}${
        event.byTimeout ? ' (timeout)' : ''
      }`;
    }
    case 'street-dealt':
      return `${event.street.toUpperCase()}   ${event.cards.join('  ')}`;
    case 'uncalled-returned':
      return `صندلی ${event.seat + 1} — بازگشت ${bb(event.amount, bigBlind)}`;
    case 'pots-built':
      return `پات: ${event.pots.map((pot) => bb(pot.amount, bigBlind)).join(' + ')}`;
    case 'showdown':
      return `شودان — ${event.shown
        .map((entry) => `صندلی ${entry.seat + 1}: ${entry.cards.join(' ')}`)
        .join(' · ')}`;
    case 'awarded':
      return `صندلی ${event.seat + 1} برد ${bb(event.amount, bigBlind)}`;
    case 'hand-complete':
      return '—';
    default:
      return '';
  }
}

function formatWhen(iso: string): string {
  const date = new Date(iso);
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}
